import {Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';

import {
  getFilesFromInput,
  readFileAsDataURL,
} from '../../../../../libs/shared/helpers/src/browser/domHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import transparentPattern from '../../../../assets/images/transparency.gif';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {ApiChannel, ChannelFrontChatModel} from '../../../../models/api/channelModel';
import {putApiAsync} from '../../../../services/http/http';
import {updateDataAction} from '../../../../store/data/dataGroupsActions';
import {SettingsFieldLine} from '../../../common/settingsFieldLine';
import {useInvalidateChannel} from '../../../fetchers/channels';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 * Props.
 */

interface SettingsChatWidgetLogoProps extends SettingsCommonChannelFieldProps<ChannelFrontChatModel> {}

/*
 * Style.
 */

const StyledLogoDiv = styled.div<{$logoUrl?: string}>`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  ${(p) => addLogoStyles(p.$logoUrl)}
`;

function addLogoStyles(logoUrl?: string) {
  if (!logoUrl) {
    return css`
      background-image: url(${transparentPattern});
      background-repeat: repeat;
      background-size: 16px 16px;
    `;
  }

  return css`
    background-image: url(${logoUrl});
    background-size: contain;
  `;
}

const StyledUploadLogoDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 16px;
  align-items: center;
`;

/*
 * Component.
 */

export const SettingsChatWidgetLogo: FC<SettingsChatWidgetLogoProps> = (props) => {
  const {edited, setDirty} = props;
  const dispatch = useAppDispatch();
  const invalidateChannel = useInvalidateChannel();
  const [optimisticLogoUrl, setOptimisticLogoUrl] = useState<string>();

  const onUploadLogoClick = async () => {
    const files = await getFilesFromInput({accept: 'image/png,image/jpeg,image/gif'});
    const fd = new FormData();
    fd.append('widget_logo', files[0]);
    const dataURL = await readFileAsDataURL(files[0]);

    setOptimisticLogoUrl(dataURL);
    const updatedChannel = await putApiAsync(ApiChannel, `${edited.url}/widget_logo`, fd, {
      timeout: 10 * 1000,
    });
    dispatch(
      updateDataAction({
        channels: [updatedChannel],
      }),
    );
    invalidateChannel(edited.id);
    setDirty();
  };
  const logoUrl = useMemo(() => {
    if (optimisticLogoUrl) {
      return optimisticLogoUrl;
    }
    if (edited.preferences.widgetHasUploadedLogo) {
      return `https://chat.frontapp.com/${edited.preferences.widgetSettingsPath}/logo?v=${edited.updatedAt}`;
    }

    return undefined;
  }, [
    edited.preferences.widgetHasUploadedLogo,
    edited.preferences.widgetSettingsPath,
    edited.updatedAt,
    optimisticLogoUrl,
  ]);
  return (
    <SettingsFieldLine
      title={<Trans id="settingschannel-widgetlogo-title">Chat widget logo</Trans>}
      subtitle={
        <Trans id="settingschannel-widgetlogo-subtitle">
          Upload your company&apos;s logo for your chat widget
        </Trans>
      }
      content={
        <StyledUploadLogoDiv>
          <StyledLogoDiv $logoUrl={logoUrl} />
          <Button variant={ButtonVariantsEnum.SECONDARY} isRounded onClick={onUploadLogoClick}>
            <Trans id="settingschannel-upload">Upload</Trans>
          </Button>
        </StyledUploadLogoDiv>
      }
    />
  );
};
